import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Select } from '@domgen/base-design-system-2.0/atoms/Select';
import { ClientLogo } from './assets/icons/ClientLogo';
import { useTranslation } from './hooks/useTranslation';
import { useResolvedLandingVariant } from './hooks/useResolvedLandingVariant';
import { getVariantCopy } from './utils/landing/getVariantCopy';
import { Locales } from './models/locale/locales';
import { isNewUxVariant } from './models/landing/variant';
import { PostHogPageView } from './services/posthog/PageView';
import { parseText } from './utils/string/parseText';
import { getUrlLocale } from './utils/translation/country';

//TODO crate Naavlink component + styles

const styles = {
  base: 'w-fit items-center justify-center text-(--color-footer-text-link) underline text-body-strong tablet:text-body-large-strong cursor-pointer',
  focus:
    'focus:ring-3 focus:ring-border-focus-1 active:ring-0 focus:py-0 focus:outline-none focus:border-transparent rounded-radius-200 ',
  visited: 'visited:text-text-link-visited',
  iconClass: `transition-colors`,
  text: `flex flex-inline items-center active:rounded-md border-b active:border-transparent active:bg-bg-primary group-focus:border-transparent group-hover:border-transparent px-space-50`,
};

const Layout = () => {
  const { locale, setLocale, data } = useTranslation();
  // The ** footnote is variant-specific on LP3 (registration-flavoured, no
  // hotline wording), so resolve the shown variant's copy for the small print.
  const { variant } = useResolvedLandingVariant();
  const smallPrint = data ? getVariantCopy(data, variant) : undefined;
  const location = useLocation();

  const handleChange = (newLocale: string) => {
    // Read the URL through the normalising reader so a mis-cased param (e.g.
    // `es-es`) compares equal to the canonical state value. Only a genuinely
    // different URL locale is worth syncing before applying the new choice.
    const languageParam = getUrlLocale(location.search);
    if (languageParam && languageParam !== locale) {
      setLocale(languageParam);
    }
    setLocale(newLocale as Locales);
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.set('lang', newLocale);
    window.history.replaceState({}, '', newUrl);
  };

  const languageList = data?.languageList
    ? data.languageList.filter((language) => {
        //Filter UK from language list on pages where it is not available
        if (location.pathname.includes('/cookies') || location.pathname.includes('/terms'))
          return language.value !== 'en-EN';
        return true;
      })
    : [];

  return (
    <>
      <PostHogPageView />
      <div className="min-h-screen">
        <header className="mx-auto w-11/12 md:w-9/12 py-1">
          <div className="flex py-space-400 content-between justify-between items-center">
            <NavLink to="/">
              <ClientLogo />
            </NavLink>
            <div className="w-50 md:w-70">
              <Select
                placeholder={data?.languageSwitcherPlaceholder}
                options={languageList}
                onChange={handleChange}
                value={locale ?? ''}
              />
            </div>
          </div>
        </header>
        <main>
          <Outlet />
        </main>
        <footer className="w-full bg-(--color-footer-bg) border-b-8 border-(--color-brand-blue) flex justify-center">
          <div className="w-11/12 md:max-w-9/12 gap-3 py-space-700">
            <ul className="pb-space-700 flex space-evenly items-center justify-center flex-col md:flex-row md:justify-between ">
              {data?.footerLinks
                // The new-UX (`-v1` / `-v2`) experiences drop the cookie-policy footer
                // link (DPO-approved 2026-08-27 — the site stores no cookies);
                // the live design keeps it, so both A/B arms match what was
                // approved for each.
                .filter(
                  (footerLink) => !(isNewUxVariant(variant) && footerLink.link === '/cookies'),
                )
                .map(
                  (
                    footerLink: {
                      text: string;
                      link: string;
                    },
                    index: number,
                  ) => (
                    <li key={index} className="mb-space-500">
                      <NavLink
                        className={`${styles.base} ${styles.visited} ${styles.focus}`}
                        to={footerLink.link}
                      >
                        {footerLink.text}
                      </NavLink>
                    </li>
                  ),
                )}
            </ul>
            <div className="flex flex-col gap-space-500 w-full text-center">
              {/* Asterisk hints (*, **) live INSIDE the footer band to match the
                  live Beko site (single block: hints → disclaimer → copyright). */}
              {data?.heroAsterix?.[0] && (
                <p>{parseText(data.heroAsterix[0], data.heroAsterixLinks ?? [])}</p>
              )}
              {smallPrint?.phoneNumberAsterix?.[0] && (
                <p>
                  {parseText(
                    smallPrint.phoneNumberAsterix[0],
                    smallPrint.phoneNumberAsterixLinks ?? [],
                  )}
                </p>
              )}
              {data?.additionalSmallPrint && (
                <p>{parseText(data.additionalSmallPrint, data.additionalSmallPrintLinks ?? [])}</p>
              )}
              <p>{data?.footerCopy}</p>
              <p>
                © {new Date().getFullYear()} Domestic & General Group Limited. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};
export default Layout;
